/**
 * WarehouseAssignmentsPanel
 * Tab 1 — Active assignments: persons + vehicles (status: assigned / received)
 * Tab 2 — Recent returns: /api/warehouse/returns/
 */
import { getAccessToken } from '../../../auth/tokenStore';
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Package, Users, Car, RotateCcw, RefreshCw, Loader2, CheckCircle2 } from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || '';

const fmtDate = s => {
  if (!s) return '—';
  return new Date(s).toLocaleDateString('pl-PL', { day: 'numeric', month: 'short' });
};

const initials = name =>
  (name || '').split(' ').map(p => p[0]).join('').slice(0, 2).toUpperCase() || '?';

const COLORS = ['#3B82F6','#10B981','#F59E0B','#8B5CF6','#EC4899','#14B8A6','#F97316'];
const userColor = name =>
  COLORS[(name || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0) % COLORS.length];

const CONDITION_CFG = {
  good:     { label: 'Good',     cls: 'bg-emerald-50 text-emerald-600' },
  damaged:  { label: 'Damaged',  cls: 'bg-rose-50 text-rose-500' },
  lost:     { label: 'Lost',     cls: 'bg-slate-100 text-slate-500' },
  consumed: { label: 'Consumed', cls: 'bg-amber-50 text-amber-600' },
};

const TABS = [
  { id: 'assignments', label: 'Assignments', icon: Users },
  { id: 'returns',     label: 'Returns',     icon: RotateCcw },
];

const SectionHeader = ({ icon: Icon, label, count }) => (
  <div className="flex items-center gap-1.5 px-3 py-1.5 bg-steel-50 border-b border-steel-100 sticky top-0 z-10">
    <Icon size={11} className="text-slate-400" />
    <span className="text-[9px] font-semibold text-slate-400 uppercase tracking-wide">
      {label} · {count}
    </span>
  </div>
);

const WarehouseAssignmentsPanel = () => {
  const [tab,      setTab]      = useState('assignments');
  const [persons,  setPersons]  = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [returns,  setReturns]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);

  const load = useCallback(() => {
    setLoading(true); setError(null);
    const headers = { Authorization: `Bearer ${getAccessToken()}` };
    const toArr = d => d?.results ?? (Array.isArray(d) ? d : []);

    Promise.all([
      // all person warehouse-issues (one call covers both assignments + returns)
      axios.get(`${API_BASE}/api/warehouse/warehouse-issues/`, { headers, params: { issue_type: 'person' } }),
      // direct PersonItemAssignment (admin / mobile)
      axios.get(`${API_BASE}/api/warehouse/person-assignments/`, { headers, params: { status: 'assigned' } }),
      axios.get(`${API_BASE}/api/warehouse/person-assignments/`, { headers, params: { status: 'received' } }),
      // vehicle assignments
      axios.get(`${API_BASE}/api/warehouse/vehicle-assignments/`, { headers, params: { status: 'assigned' } }),
      axios.get(`${API_BASE}/api/warehouse/vehicle-assignments/`, { headers, params: { status: 'delivered' } }),
      // PersonItemAssignment returns
      axios.get(`${API_BASE}/api/warehouse/person-assignments/`,  { headers, params: { status: 'returned' } }),
      // vehicle returns
      axios.get(`${API_BASE}/api/warehouse/vehicle-assignments/`, { headers, params: { status: 'returned' } }),
    ])
      .then(([wiRes, paRes, prRes, vaRes, vdRes, pRetRes, vRetRes]) => {
        const allPersonIssues = toArr(wiRes.data);

        // ── ASSIGNMENTS: issues still active (items out) ───────────────
        const wiActive = allPersonIssues
          .filter(wi => ['issued', 'partially_returned'].includes(wi.status))
          .flatMap(wi => (wi.items?.length ? wi.items : [null]).map(it => ({
            _src: 'wi', id: `wi-${wi.id}-${it?.id ?? 0}`,
            person_name: wi.assigned_to_person || '—',
            item_name: it?.item_name || it?.inventory_item?.name || '—',
            quantity: it?.quantity_issued ?? it?.quantity ?? '—',
            assigned_date: wi.issued_at,
          })));

        const paActive = [...toArr(paRes.data), ...toArr(prRes.data)]
          .map(p => ({ _src: 'pa', ...p }));

        setPersons([...wiActive, ...paActive]);
        setVehicles([...toArr(vaRes.data), ...toArr(vdRes.data)]);

        // ── RETURNS ────────────────────────────────────────────────────
        // warehouse-issues that have been (partially or fully) returned
        const wiRet = allPersonIssues
          .filter(wi => ['partially_returned', 'fully_returned'].includes(wi.status))
          .flatMap(wi => (wi.items?.length ? wi.items : [null]).map(it => ({
            _key: `wi-${wi.id}-${it?.id ?? 0}`,
            _type: 'person',
            _date: wi.updated_at,
            _condition: null,
            item_name: it?.item_name || it?.inventory_item?.name || '—',
            _who: wi.assigned_to_person || '—',
          })));

        const pRet = toArr(pRetRes.data).map(r => ({
          _key: `p-${r.id}`, _type: 'person',
          _date: r.return_date, _condition: r.return_condition,
          item_name: r.item_name, _who: r.person_name,
        }));
        const vRet = toArr(vRetRes.data).map(r => ({
          _key: `v-${r.id}`, _type: 'vehicle',
          _date: r.return_date, _condition: r.return_condition,
          item_name: r.item_name, _who: r.vehicle_info,
        }));

        setReturns(
          [...wiRet, ...pRet, ...vRet]
            .sort((a, b) => new Date(b._date || 0) - new Date(a._date || 0))
            .slice(0, 60)
        );
      })
      .catch(() => setError('Could not load assignment data.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const totalAssignments = persons.length + vehicles.length;

  return (
    <div className="h-full flex flex-col bg-white overflow-hidden">

      {/* header */}
      <div className="flex items-center justify-between px-3 py-2 border-b-2 border-brand-500 bg-steel-50 flex-shrink-0">
        <div className="flex items-center gap-2">
          <div className="p-1 rounded-lg bg-gradient-to-br from-brand-500 to-brand-600">
            <Package size={13} className="text-white" />
          </div>
          <span className="text-sm font-semibold text-slate-800">Assignments & Returns</span>
          {!loading && tab === 'assignments' && totalAssignments > 0 && (
            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-600">
              {totalAssignments} active
            </span>
          )}
          {!loading && tab === 'returns' && returns.length > 0 && (
            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500">
              {returns.length} returns
            </span>
          )}
        </div>
        <button onClick={load} className="p-1 rounded hover:bg-steel-200 text-slate-400 hover:text-slate-600 transition-colors">
          <RefreshCw size={12} />
        </button>
      </div>

      {/* tabs */}
      <div className="flex border-b border-steel-100 flex-shrink-0">
        {TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors border-b-2 -mb-px ${
              tab === t.id
                ? 'border-brand-500 text-brand-600'
                : 'border-transparent text-slate-400 hover:text-slate-600'
            }`}
          >
            <t.icon size={12} />
            {t.label}
          </button>
        ))}
      </div>

      {loading && (
        <div className="flex-1 flex items-center justify-center">
          <Loader2 size={20} className="animate-spin text-slate-300" />
        </div>
      )}
      {!loading && error && (
        <div className="flex-1 flex items-center justify-center text-xs text-rose-400 px-4 text-center">{error}</div>
      )}

      {/* ── ASSIGNMENTS TAB ── */}
      {!loading && !error && tab === 'assignments' && (
        <div className="flex-1 min-h-0 overflow-y-auto">

          {persons.length > 0 && (
            <div>
              <SectionHeader icon={Users} label="Employees" count={persons.length} />
              {persons.map((item, i) => (
                <div key={item.id}
                  className={`flex items-center gap-2 px-3 py-2 border-b border-steel-50 hover:bg-steel-50 transition-colors ${i % 2 === 0 ? '' : 'bg-slate-50/30'}`}>
                  <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-white"
                    style={{ backgroundColor: userColor(item.person_name), fontSize: 9, fontWeight: 700 }}>
                    {initials(item.person_name)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-slate-700 truncate leading-tight">{item.person_name || '—'}</p>
                    <p className="text-[10px] text-slate-400 truncate leading-tight">{item.item_name || '—'}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-xs font-bold text-slate-700">{item.quantity} pcs</p>
                    <p className="text-[9px] text-slate-400">{fmtDate(item.assigned_date)}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {vehicles.length > 0 && (
            <div>
              <SectionHeader icon={Car} label="Vehicles" count={vehicles.length} />
              {vehicles.map((item, i) => (
                <div key={item.id}
                  className={`flex items-center gap-2 px-3 py-2 border-b border-steel-50 hover:bg-steel-50 transition-colors ${i % 2 === 0 ? '' : 'bg-slate-50/30'}`}>
                  <div className="w-6 h-6 rounded-full bg-steel-100 flex items-center justify-center flex-shrink-0">
                    <Car size={12} className="text-slate-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-slate-700 truncate leading-tight">{item.vehicle_info || '—'}</p>
                    <p className="text-[10px] text-slate-400 truncate leading-tight">{item.item_name || '—'}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-xs font-bold text-slate-700">{item.quantity} pcs</p>
                    <p className="text-[9px] text-slate-400">{fmtDate(item.assigned_date)}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {totalAssignments === 0 && (
            <div className="flex flex-col items-center justify-center gap-2 text-slate-300 py-10">
              <CheckCircle2 size={28} />
              <span className="text-xs">No active assignments</span>
            </div>
          )}
        </div>
      )}

      {/* ── RETURNS TAB ── */}
      {!loading && !error && tab === 'returns' && (
        <div className="flex-1 min-h-0 overflow-y-auto">
          {returns.length > 0 && (
            <div className="flex items-center gap-2 px-3 py-1 border-b border-steel-100 bg-steel-50 sticky top-0">
              <span className="flex-1 text-[9px] font-semibold text-slate-400 uppercase tracking-wide">Item / Source</span>
              <span className="w-18 text-[9px] font-semibold text-slate-400 uppercase tracking-wide">Condition</span>
              <span className="w-12 text-[9px] font-semibold text-slate-400 uppercase tracking-wide text-right">Data</span>
            </div>
          )}

          {returns.map((item, i) => {
            const cfg = CONDITION_CFG[item._condition] || CONDITION_CFG.good;
            const TypeIcon = item._type === 'vehicle' ? Car : Users;
            return (
              <div key={item._key}
                className={`flex items-center gap-2 px-3 py-2 border-b border-steel-50 hover:bg-steel-50 transition-colors ${i % 2 === 0 ? '' : 'bg-slate-50/30'}`}>
                <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 bg-steel-100">
                  <TypeIcon size={11} className="text-slate-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-slate-700 truncate leading-tight">{item.item_name || '—'}</p>
                  <p className="text-[10px] text-slate-400 truncate leading-tight">{item._who || '—'}</p>
                </div>
                <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full whitespace-nowrap flex-shrink-0 ${cfg.cls}`}>
                  {cfg.label}
                </span>
                <span className="w-12 text-[9px] text-slate-400 text-right flex-shrink-0">{fmtDate(item._date)}</span>
              </div>
            );
          })}

          {returns.length === 0 && (
            <div className="flex flex-col items-center justify-center gap-2 text-slate-300 py-10">
              <RotateCcw size={28} />
              <span className="text-xs">No returns</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default WarehouseAssignmentsPanel;
